/**
 * Content Packs API
 *
 * Content pack management endpoints for themes, backgrounds, etc.
 * Corresponds to backend /v1/admin/content-packs/*
 */

import apiClient from './client';

// ============================================================================
// Typed Metadata Interfaces
// ============================================================================

export interface ViewPort {
  width: number;
  height: number;
}

export interface TextBox {
  width: number;
  height: number;
  top: number;
  left: number;
}

export interface ItemDescription {
  theme?: string;
  emotion?: string;
  keywords?: string[];
}

export interface FontColorPalette {
  mode: 'palette';
  light_bg: string;
  mid_bg?: string;
  dark_bg: string;
}

export interface LivePhotoMeta {
  video_object_name?: string;
}

export interface ItemRenderMetadata {
  view_port?: ViewPort;
  text_box?: TextBox;
  font_color?: string | FontColorPalette;
  outline_color?: string;
  rotation_angle?: number;
  font_size?: number;
  layout_vertical_stack?: boolean;
  layout_balanced_wrap?: boolean;
  layout_reject_unbalanced?: boolean;
  font_scale_x?: number;
  line_spacing?: number;
  text_align?: 'left' | 'center' | 'right';
  text_effect?: 'none' | 'outline' | 'shadow';
  shadow_blur?: number;
  shadow_opacity?: number;
  shadow_color?: string;
  shadow_distance?: number;
  shadow_angle?: number;
  shadow_spread?: number;
  shadow_blend_mode?: 'normal' | 'multiply';
  display_name?: string;
  description?: ItemDescription;
  live_photo?: LivePhotoMeta;
}

export interface OverlayConfig {
  enabled: boolean;
  filename?: string;
  position?: string;
}

export interface PackMetadata {
  overlay?: OverlayConfig;
  default_metadata?: ItemRenderMetadata;
  /**
   * Pack-level default font. When a workflow node doesn't specify `font_id`,
   * the executor falls back to this value before the global default.
   * Backed by `content_packs.pack_config->'default_font_id'` in PostgreSQL.
   */
  default_font_id?: number | null;
}

// ============================================================================
// Content Pack Types
// ============================================================================

export interface ContentPack {
  id: string;
  type: string;
  name: string;
  display_name: string;
  description: string;
  metadata: PackMetadata;
  status: 'draft' | 'active' | 'archived';
  is_default: boolean;
  sort_order: number;
  item_count: number;
  created_by?: string;
  created_at?: string;
  updated_at?: string;
  items?: ContentPackItem[];
}

export interface ContentPackItem {
  id: string;
  filename: string;
  object_name: string;
  item_type: string;
  metadata: ItemRenderMetadata;
  file_size: number;
  content_type: string;
  sort_order: number;
  created_at?: string;
  thumbnail_url?: string;
}

export interface ContentPackListResponse {
  packs: ContentPack[];
  total: number;
}

export interface ContentPackCreate {
  type: string;
  name: string;
  display_name: string;
  description?: string;
  metadata?: Record<string, any>;
  status?: string;
}

export interface ContentPackUpdate {
  display_name?: string;
  description?: string;
  metadata?: PackMetadata;
  status?: string;
  sort_order?: number;
  is_default?: boolean;
}

// ============================================================================
// Preview types & helpers
// ============================================================================

export interface ColorInfo {
  configMode: 'auto' | 'fixed' | 'palette'
  resolvedColor: string
  luminance: number | null
  warmth: number | null
  luminanceBucket: 'light' | 'mid' | 'dark' | 'invalid' | ''
  fallbackReason: '' | 'no_mid_bg_auto' | 'text_area_invalid'
}

export interface TextAreaInfo {
  xPct: number; yPct: number; wPct: number; hPct: number
  xPx: number; yPx: number; wPx: number; hPx: number
  imgW: number; imgH: number
}

export interface PreviewResult {
  blob: Blob
  colorInfo: ColorInfo
  textArea: TextAreaInfo
}

function _parseNum(val: string): number {
  const n = parseFloat(val)
  return isNaN(n) ? 0 : n
}

 
function _extractDiagnostics(headers: any): { colorInfo: ColorInfo; textArea: TextAreaInfo } {
  const get = (key: string): string => String(headers?.[key] || headers?.get?.(key) || '')
  return {
    colorInfo: {
      configMode: get('x-color-config-mode') as ColorInfo['configMode'],
      resolvedColor: get('x-color-resolved'),
      luminance: get('x-color-luminance') && get('x-color-luminance') !== 'None' ? parseFloat(get('x-color-luminance')) : null,
      warmth: get('x-color-warmth') && get('x-color-warmth') !== 'None' ? parseFloat(get('x-color-warmth')) : null,
      luminanceBucket: get('x-color-bucket') as ColorInfo['luminanceBucket'],
      fallbackReason: get('x-color-fallback') as ColorInfo['fallbackReason'],
    },
    textArea: {
      xPct: _parseNum(get('x-textarea-x-pct')),
      yPct: _parseNum(get('x-textarea-y-pct')),
      wPct: _parseNum(get('x-textarea-w-pct')),
      hPct: _parseNum(get('x-textarea-h-pct')),
      xPx: _parseNum(get('x-textarea-x-px')),
      yPx: _parseNum(get('x-textarea-y-px')),
      wPx: _parseNum(get('x-textarea-w-px')),
      hPx: _parseNum(get('x-textarea-h-px')),
      imgW: _parseNum(get('x-textarea-img-w')),
      imgH: _parseNum(get('x-textarea-img-h')),
    },
  }
}

export const contentPacksApi = {
  /**
   * List content packs with optional type/status filters
   */
  list: async (type?: string, status?: string): Promise<ContentPackListResponse> => {
    const params: Record<string, string> = {};
    if (type) params.type = type;
    if (status) params.status = status;
    const response = await apiClient.get<ContentPackListResponse>('/content-packs', { params });
    return response.data;
  },

  /**
   * Get a single content pack by ID (includes items)
   */
  get: async (packId: string): Promise<ContentPack> => {
    const response = await apiClient.get<ContentPack>(`/content-packs/${packId}`);
    return response.data;
  },

  /**
   * Create a new content pack
   */
  create: async (data: ContentPackCreate): Promise<ContentPack> => {
    const response = await apiClient.post<ContentPack>('/content-packs', data);
    return response.data;
  },

  /**
   * Update an existing content pack
   */
  update: async (packId: string, data: ContentPackUpdate): Promise<ContentPack> => {
    const response = await apiClient.put<ContentPack>(`/content-packs/${packId}`, data);
    return response.data;
  },

  /**
   * Delete a content pack
   */
  delete: async (packId: string): Promise<void> => {
    await apiClient.delete(`/content-packs/${packId}`);
  },

  /**
   * Upload items (files) to a content pack
   */
  uploadItems: async (packId: string, files: File[], itemType = 'background'): Promise<ContentPackItem[]> => {
    const formData = new FormData();
    files.forEach(f => formData.append('files', f));
    const response = await apiClient.post<ContentPackItem[]>(
      `/content-packs/${packId}/items?item_type=${itemType}`,
      formData,
      { headers: { 'Content-Type': 'multipart/form-data' } }
    );
    return response.data;
  },

  /**
   * Update item metadata
   */
  updateItemMetadata: async (packId: string, itemId: string, metadata: ItemRenderMetadata): Promise<ContentPackItem> => {
    const response = await apiClient.put<ContentPackItem>(
      `/content-packs/${packId}/items/${itemId}`,
      { metadata }
    );
    return response.data;
  },

  /**
   * Delete a single item from a content pack
   */
  deleteItem: async (packId: string, itemId: string): Promise<void> => {
    await apiClient.delete(`/content-packs/${packId}/items/${itemId}`);
  },

  /**
   * Reorder items within a content pack
   */
  reorderItems: async (packId: string, itemOrders: Array<{ id: string; sort_order: number }>): Promise<void> => {
    await apiClient.put(`/content-packs/${packId}/items/reorder`, { item_orders: itemOrders });
  },

  /**
   * Generate a server-rendered preview for a specific item.
   * Returns blob + font color resolution diagnostics from response headers.
   */
  previewItem: async (packId: string, itemId: string, metadata: ItemRenderMetadata, phrase = '幸运符文字预览'): Promise<PreviewResult> => {
    const response = await apiClient.post(
      `/content-packs/${packId}/items/${itemId}/preview`,
      { metadata, phrase },
      { responseType: 'blob' }
    );
    const diag = _extractDiagnostics(response.headers);
    return { blob: response.data, ...diag };
  },

  /**
   * Generate a server-rendered preview using a random item from the pack.
   * Returns blob + font color + text area diagnostics from response headers.
   */
  previewPack: async (packId: string, metadata: ItemRenderMetadata, phrase = '幸运符文字预览'): Promise<PreviewResult> => {
    const response = await apiClient.post(
      `/content-packs/${packId}/preview`,
      { metadata, phrase },
      { responseType: 'blob' }
    );
    const diag = _extractDiagnostics(response.headers);
    return { blob: response.data, ...diag };
  },

  /**
   * One-time migration from filesystem to OSS
   */
  migrateFilesystem: async (): Promise<any> => {
    const response = await apiClient.post('/content-packs/migrate-filesystem');
    return response.data;
  },
};
